// App.js
import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import ClientServices from './pages/ClientServices';
import AdminDashboard from './pages/AdminDashboard';
import AdminProducts from './pages/AdminProducts';
import AdminCategories from './pages/AdminCategories';
import AdminManicuristas from './pages/AdminManicuristas';
import AdminCarousel from './pages/AdminCarousel';
import AdminSettings from './pages/AdminSettings';
import PublicProducts from './pages/PublicProducts';
import AdminBookings from './pages/AdminBookings';
import AdminSchedule from './pages/AdminSchedule'; // Importa el componente AdminSchedule
import AdminEvaluaciones from './pages/AdminEvaluaciones';
import AccessRestricted from './pages/AccessRestricted';
import LoginModal from './components/LoginModal';
import Layout from './components/Layout';
import Register from './pages/Register';
import RestorePassword from './pages/RestorePassword';
import ReservasUsuarios from './pages/ReservasUsuarios';
import BotAvatar from './components/BotAvatar';
import ChatBotUI from './components/ChatBotUI';
import { NotificationProvider, useNotify } from './components/NotificationProvider';
import { API_BASE, isAdminRole, isSuperAdminRole } from './utils/helpers';

import { loginUsuario, registrarUsuario } from './api/usuarios';

// Duración configurable del overlay de carga tras login (ms)
const LOGIN_LOADING_MS = 2500;

// Componente estable (fuera de AppInner) para proteger rutas de admin
const ProtectedAdminRoute = ({ children, currentUser }) => {
  const [status, setStatus] = useState('checking'); // checking | active | inactive | error
  const didCleanup = useRef(false);
  useEffect(() => {
    let alive = true;
    // Si no hay admin, marcar inactivo para renderizar redirección de forma segura
    if (!currentUser || !isAdminRole(currentUser.role)) {
      setStatus('inactive');
      return () => { alive = false; didCleanup.current = true; };
    }
    const check = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/usuarios/${currentUser.id}/status`);
        if (!res.ok) { if (alive) setStatus('active'); return; }
        const data = await res.json();
        const activo = String((data && data.estado) || 'activo').toLowerCase() === 'activo';
        if (alive) setStatus(activo ? 'active' : 'inactive');
      } catch {
        if (alive) setStatus('active');
      }
    };
    check();
    return () => { alive = false; didCleanup.current = true; };
  }, [currentUser?.id, currentUser?.role]);
  if (!currentUser || !isAdminRole(currentUser.role)) {
    return <Navigate to="/productos" replace />;
  }
  if (status === 'checking') return null;
  if (status === 'inactive') {
    try { localStorage.removeItem('user'); } catch {}
    return <Navigate to="/productos" replace />;
  }
  return children;
};

const ProtectedSuperAdminRoute = ({ children, currentUser }) => {
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    let alive = true;
    if (!currentUser || !isSuperAdminRole(currentUser.role)) {
      setStatus('inactive');
      return () => { alive = false; };
    }

    const check = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/usuarios/${currentUser.id}/status`);
        if (!res.ok) { if (alive) setStatus('active'); return; }
        const data = await res.json();
        const activo = String((data && data.estado) || 'activo').toLowerCase() === 'activo';
        if (alive) setStatus(activo ? 'active' : 'inactive');
      } catch {
        if (alive) setStatus('active');
      }
    };
    check();
    return () => { alive = false; };
  }, [currentUser?.id, currentUser?.role]);

  if (!currentUser || !isSuperAdminRole(currentUser.role)) {
    return <Navigate to="/productos" replace />;
  }
  if (status === 'checking') return null;
  if (status === 'inactive') {
    try { localStorage.removeItem('user'); } catch {}
    return <Navigate to="/productos" replace />;
  }
  return children;
};

const ProtectedUserRoute = ({ children, currentUser }) => {
  const [status, setStatus] = useState('checking');
  useEffect(() => {
    let alive = true;
    if (!currentUser) {
      setStatus('inactive');
      return () => { alive = false; };
    }
    const check = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/usuarios/${currentUser.id}/status`);
        if (!res.ok) { if (alive) setStatus('active'); return; }
        const data = await res.json();
        const activo = String((data && data.estado) || 'activo').toLowerCase() === 'activo';
        if (alive) setStatus(activo ? 'active' : 'inactive');
      } catch {
        if (alive) setStatus('active');
      }
    };
    check();
    return () => { alive = false; };
  }, [currentUser?.id]);
  if (!currentUser) return <Navigate to="/productos" replace />;
  if (status === 'checking') return null;
  if (status === 'inactive') {
    try { localStorage.removeItem('user'); } catch {}
    return <Navigate to="/productos" replace />;
  }
  return children;
};

const AppInner = () => {
  const [currentUser, setCurrentUser] = useState(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });
  // Caducidad por inactividad
  const SESSION_IDLE_MS = 3 * 60 * 1000; // 3 minutos
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [authLoading, setAuthLoading] = useState(false); // overlay de carga tras login
  const [progress, setProgress] = useState(0); // progreso visual durante la carga
  const [chatbotEnabled, setChatbotEnabled] = useState(() => {
    try {
      return localStorage.getItem('chatbot_enabled') !== 'false';
    } catch {
      return true;
    }
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { success: notifySuccess, error: notifyError, info: notifyInfo, remove } = useNotify();
  
  const WARNING_WINDOW_MS = 30000; // avisar 30s antes de expirar
  const [warnScheduled, setWarnScheduled] = useState(false);
  const [idleWarningVisible, setIdleWarningVisible] = useState(false);
  const [idleSecondsLeft, setIdleSecondsLeft] = useState(0);

  useEffect(() => {
    if (currentUser) {
      localStorage.setItem('user', JSON.stringify(currentUser));
    } else {
      localStorage.removeItem('user');
      localStorage.removeItem('sessionExpiresAt');
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser?.id) return undefined;

    let alive = true;
    let checking = false;

    const syncCurrentUser = async () => {
      if (checking || document.visibilityState === 'hidden') return;
      checking = true;
      try {
        const response = await fetch(
          `${API_BASE}/api/usuarios/${currentUser.id}/status?_=${Date.now()}`,
          { cache: 'no-store' }
        );
        if (!response.ok) return;

        const data = await response.json();
        if (!alive) return;

        if (String(data.estado || 'activo').toLowerCase() !== 'activo') {
          localStorage.removeItem('user');
          localStorage.removeItem('sessionExpiresAt');
          setCurrentUser(null);
          navigate('/productos', { replace: true });
          return;
        }

        const role = String(data.rol || '').toLowerCase();
        if (role && role !== currentUser.role) {
          setCurrentUser((previous) => (
            previous?.id === currentUser.id ? { ...previous, role } : previous
          ));
        }
      } catch {
        // Keep the current session during temporary API/network failures.
      } finally {
        checking = false;
      }
    };

    syncCurrentUser();
    const interval = setInterval(syncCurrentUser, 15000);
    window.addEventListener('focus', syncCurrentUser);
    document.addEventListener('visibilitychange', syncCurrentUser);

    return () => {
      alive = false;
      clearInterval(interval);
      window.removeEventListener('focus', syncCurrentUser);
      document.removeEventListener('visibilitychange', syncCurrentUser);
    };
  }, [currentUser?.id, currentUser?.role, navigate]);

  useEffect(() => {
    try {
      localStorage.setItem('chatbot_enabled', chatbotEnabled ? 'true' : 'false');
    } catch {}
  }, [chatbotEnabled]);

  // Al cargar la app, validar caducidad de sesión por inactividad
  useEffect(() => {
    const exp = Number(localStorage.getItem('sessionExpiresAt') || 0);
    if (exp && Date.now() > exp) {
      localStorage.removeItem('user');
      localStorage.removeItem('sessionExpiresAt');
      setCurrentUser(null);
    }
  }, []);

  // Chequeo periódico de caducidad (sin afectar F5)
  useEffect(() => {
    const timer = setInterval(() => {
      const exp = Number(localStorage.getItem('sessionExpiresAt') || 0);
      if (exp && Date.now() > exp) {
        localStorage.removeItem('user');
        localStorage.removeItem('sessionExpiresAt');
        setIdleWarningVisible(false);
        setCurrentUser(null);
        // Redirige al catálogo público al caducar por inactividad
        navigate('/productos', { replace: true });
      }
    }, 60 * 1000); // cada minuto
    return () => clearInterval(timer);
  }, []);

  // Resetear caducidad con actividad del usuario
  useEffect(() => {
    const bumpExpiry = () => {
      // Si el modal de advertencia está visible, no extender sesión automáticamente.
      if (idleWarningVisible) return;
      if (localStorage.getItem('user')) {
        localStorage.setItem('sessionExpiresAt', String(Date.now() + SESSION_IDLE_MS));
        setWarnScheduled(false);
      }
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(evt => window.addEventListener(evt, bumpExpiry, { passive: true }));
    // Inicializa vencimiento al cargar si hay sesión
    bumpExpiry();
    return () => {
      events.forEach(evt => window.removeEventListener(evt, bumpExpiry));
    };
  }, []);

  // Aviso previo a caducidad por inactividad (~30s antes)
  useEffect(() => {
    const interval = setInterval(() => {
      const exp = Number(localStorage.getItem('sessionExpiresAt') || 0);
      if (!exp) return;
      const now = Date.now();
      if (now >= exp) {
        setWarnScheduled(false);
        setIdleWarningVisible(false);
        return; // el cierre efectivo lo maneja el chequeo principal
      }
      const remaining = exp - now;
      if (!warnScheduled && remaining <= WARNING_WINDOW_MS) {
        setWarnScheduled(true);
        notifyInfo('Tu sesión se cerrará por inactividad en ~30s', { duration: 5000 });
      } else if (warnScheduled && remaining > WARNING_WINDOW_MS) {
        setWarnScheduled(false);
      }
    }, 5000); // chequeo ligero cada 5s para avisos
    return () => clearInterval(interval);
  }, [warnScheduled, notifyInfo]);

  // Mostrar modal con cuenta regresiva en la ventana de aviso
  useEffect(() => {
    const t = setInterval(() => {
      const exp = Number(localStorage.getItem('sessionExpiresAt') || 0);
      if (!exp) { setIdleWarningVisible(false); return; }
      const now = Date.now();
      const remaining = exp - now;
      if (remaining > 0 && remaining <= WARNING_WINDOW_MS) {
        setIdleWarningVisible(true);
        setIdleSecondsLeft(Math.ceil(remaining / 1000));
      } else {
        // Mantén el modal visible hasta que el usuario elija una acción
        if (!idleWarningVisible) {
          setIdleWarningVisible(false);
        }
      }
    }, 1000);
    return () => clearInterval(t);
  }, [idleWarningVisible]);

  const extendSessionNow = () => {
    localStorage.setItem('sessionExpiresAt', String(Date.now() + SESSION_IDLE_MS));
    setWarnScheduled(false);
    setIdleWarningVisible(false);
  };

  // Refuerza el bloqueo: deshabilita el scroll y clics de fondo cuando el modal está visible
  useEffect(() => {
    if (idleWarningVisible) {
      try { document.body.style.overflow = 'hidden'; } catch {}
    } else {
      try { document.body.style.overflow = ''; } catch {}
    }
    return () => { try { document.body.style.overflow = ''; } catch {} };
  }, [idleWarningVisible]);

  // Asegura que nunca quede overflow bloqueado al cambiar de ruta
  useEffect(() => {
    try { document.body.style.overflow = ''; } catch {}
  }, [location.pathname]);

  const handleLogin = async (username, password) => {
    if (authLoading) return; // evitar doble click mientras carga
    setAuthLoading(true);
    setProgress(0);
    try {
      const result = await loginUsuario({ username, password });
      if (result && result.success) {
        const role = result.user.Rol;
        const nextUser = {
          id: result.user.Id,
          username: result.user.NombreUsuario,
          nombreCompleto: result.user.NombreCompleto,
          email: result.user.Correo,
          telefono: result.user.Telefono,
          role: String(role || '').toLowerCase(),
        };
        setCurrentUser(nextUser);
        // Configura tiempo de expiración de sesión por inactividad (3 min)
        localStorage.setItem('sessionExpiresAt', String(Date.now() + SESSION_IDLE_MS));
        setIsLoginModalOpen(false);
        // Mantener la animación/overlay visible por LOGIN_LOADING_MS
        setTimeout(() => {
          setAuthLoading(false);
        }, LOGIN_LOADING_MS);
        notifySuccess(`Bienvenido ${result.user.NombreUsuario}`, { duration: 3500 });
        // Navegar según rol sin interrumpir otras rutas públicas
        if (isAdminRole(role)) {
          navigate('/admin', { replace: true });
        } else {
          // Para usuarios cliente, redirigir al home de servicios
          navigate('/', { replace: true });
        }
      } else {
        setAuthLoading(false);
        const msg = (result && result.message) || (result && result.error) || 'Credenciales inválidas';
        notifyError(msg, { duration: 4500 });
      }
    } catch (err) {
      setAuthLoading(false);
      notifyError(err.message || 'Error al iniciar sesión', { duration: 4500 });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('sessionExpiresAt');
    setIdleWarningVisible(false);
    setCurrentUser(null);
    navigate('/', { replace: true });
    notifyInfo('Sesión cerrada', { duration: 3000 });
  };

  // Redirigir solo si el usuario ya no existe y está en una ruta protegida
  // Permite rutas públicas como '/productos', '/register', '/restore-password'
  useEffect(() => {
    if (!currentUser) {
      const inProtected =
        /^\/admin(\/|$)/.test(location.pathname) ||
        /^\/mis-reservas(\/|$)/.test(location.pathname);
      const isPublic =
        location.pathname === '/productos' ||
        location.pathname === '/register' ||
        location.pathname === '/restore-password' ||
        location.pathname === '/';
      if (inProtected && !isPublic) {
        navigate('/productos', { replace: true });
      }
    }
  }, [currentUser, location.pathname, navigate]);

  // Nota: ProtectedAdminRoute se define fuera de AppInner para evitar remounts en cada render

  // Registro real: form debe traer fechaNacimiento desde <input type="date" />
  const handleRegister = async (form) => {
    try {
      // Normaliza a 'YYYY-MM-DD'
      const fechaISO = form.fechaNacimiento
        ? new Date(form.fechaNacimiento).toISOString().slice(0, 10)
        : null;

      const payload = {
        nombreUsuario: form.username,
        password: form.password,
        correo: form.email,
        rol: 'usuario',
        nombreCompleto: form.nombreCompleto || '',
        telefono: form.telefono || '',
        fechaNacimiento: fechaISO
      };

      const res = await registrarUsuario(payload);
      if (res.success) {
        notifySuccess('Usuario registrado correctamente', { duration: 4000 });
        navigate('/');
      } else {
        notifyError(res.error || 'Error registrando usuario', { duration: 5000 });
      }
    } catch (e) {
      notifyError(e?.message || 'Error registrando usuario', { duration: 5000 });
    }
  };

  // Actualiza la barra de progreso mientras esté activo el overlay de carga (LOGIN_LOADING_MS)
  useEffect(() => {
    let interval;
    if (authLoading) {
      const start = Date.now();
      setProgress(0);
      interval = setInterval(() => {
        const elapsed = Date.now() - start;
        const pct = Math.min(100, Math.round((elapsed / LOGIN_LOADING_MS) * 100));
        setProgress(pct);
      }, 100);
    } else {
      setProgress(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [authLoading]);

  const handleRestorePassword = (email) => {
    notifyInfo('Si el correo existe, recibirás instrucciones para restaurar tu contraseña.', { duration: 6000 });
  };

  // Escucha eventos globales para abrir el modal de login desde páginas públicas
  useEffect(() => {
    const openLogin = () => setIsLoginModalOpen(true);
    window.addEventListener('open-login', openLogin);
    return () => window.removeEventListener('open-login', openLogin);
  }, []);

  // Al cerrar el modal de login, eliminar la notificación persistente si existe
  useEffect(() => {
    const closeLogin = () => {
      try {
        const id = window.lastLoginPromptId;
        if (id) {
          remove(id);
          window.lastLoginPromptId = undefined;
        }
      } catch {}
    };
    window.addEventListener('close-login', closeLogin);
    return () => window.removeEventListener('close-login', closeLogin);
  }, []);

  // Tras login exitoso, si existe un producto preseleccionado desde página pública, abrir formulario
  useEffect(() => {
    if (!currentUser) return;
    try {
      const raw = localStorage.getItem('preselectProduct');
      if (raw) {
        const p = JSON.parse(raw);
        localStorage.removeItem('preselectProduct');
        navigate('/', { replace: false, state: { selectProduct: p, requireLogin: false } });
      }
    } catch {}
  }, [currentUser]);

  return (
    <Layout
      user={currentUser}
      onLogout={handleLogout}
      onLoginClick={() => setIsLoginModalOpen(true)}
      chatbotEnabled={chatbotEnabled}
      onChatbotToggle={setChatbotEnabled}
    >
      {authLoading && (
        <div className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-white/85 backdrop-blur">
          <img
            src="/images/001.gif"
            alt="Mora.Artt cargando"
            className="w-40 h-40 mb-6 drop-shadow-xl animate-pulse rounded-full object-cover"
          />
          <p className="text-pink-600 font-semibold text-xl tracking-wide animate-bounce">Ingresando a Moraartt...</p>
          <p className="text-gray-500 text-sm mt-2">Cargando...</p>
          {/* Barra de progreso 0-100% durante los 5s */}
          <div className="w-64 h-2 bg-gray-200 rounded-full overflow-hidden mt-4 shadow-inner">
            <div
              className="h-full bg-pink-600 transition-[width] duration-100 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-gray-400 text-xs mt-1">{progress}%</p>
        </div>
      )}
      {idleWarningVisible && !authLoading && currentUser && (
        <div className="fixed inset-0 z-[190] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl border border-purple-200">
            <h3 className="text-xl font-bold text-purple-700 mb-2">Sesión por inactividad</h3>
            <p className="text-gray-700 mb-1">Tu sesión se cerrará automáticamente por inactividad.</p>
            <p className="text-gray-900 font-semibold mb-4">Tiempo restante: {idleSecondsLeft}s</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleLogout}
                className="px-4 py-2 rounded-xl bg-gray-200 text-gray-700 hover:bg-gray-300"
              >Cerrar ahora</button>
              <button
                onClick={extendSessionNow}
                className="px-4 py-2 rounded-xl bg-purple-600 text-white hover:bg-purple-700"
              >Seguir conectado</button>
            </div>
          </div>
        </div>
      )}
      <Routes>
        {/* Ruta principal */}
        <Route
          path="/"
          element={<ClientServices user={currentUser} onLoginClick={() => setIsLoginModalOpen(true)} />}
        />
        
        {/* Rutas de administración protegidas */}
        <Route 
          path="/admin" 
          element={
            <ProtectedAdminRoute currentUser={currentUser}>
              <AdminDashboard user={currentUser} />
            </ProtectedAdminRoute>
          } 
        />
        <Route
          path="/admin/products"
          element={
            <ProtectedAdminRoute currentUser={currentUser}>
              <AdminProducts user={currentUser} onLoginClick={() => setIsLoginModalOpen(true)} />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/categories"
          element={
            <ProtectedAdminRoute currentUser={currentUser}>
              <AdminCategories />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/carrusel"
          element={
            <ProtectedAdminRoute currentUser={currentUser}>
              <AdminCarousel user={currentUser} />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/manicuristas"
          element={
            <ProtectedSuperAdminRoute currentUser={currentUser}>
              <AdminManicuristas user={currentUser} />
            </ProtectedSuperAdminRoute>
          }
        />
        <Route
          path="/admin/configuracion"
          element={
            <ProtectedSuperAdminRoute currentUser={currentUser}>
              <AdminSettings user={currentUser} />
            </ProtectedSuperAdminRoute>
          }
        />
        <Route
          path="/admin/bookings"
          element={
            <ProtectedAdminRoute currentUser={currentUser}>
              <AdminBookings user={currentUser} onLoginClick={() => setIsLoginModalOpen(true)} />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/evaluaciones"
          element={
            <ProtectedAdminRoute currentUser={currentUser}>
              <AdminEvaluaciones user={currentUser} onLoginClick={() => setIsLoginModalOpen(true)} />
            </ProtectedAdminRoute>
          }
        />
        
        {/* NUEVA RUTA AGENDA - Agregada aquí */}
        <Route
          path="/admin/agenda"
          element={
            <ProtectedAdminRoute currentUser={currentUser}>
              <AdminSchedule user={currentUser} onLoginClick={() => setIsLoginModalOpen(true)} />
            </ProtectedAdminRoute>
          }
        />
        
        {/* Rutas públicas */}
        <Route path="/register" element={<Register onRegister={handleRegister} />} />
  <Route path="/restore-password" element={<RestorePassword onRestore={handleRestorePassword} />} />
          <Route path="/productos" element={<PublicProducts />} />
  <Route path="/acceso-restringido" element={<AccessRestricted onLoginClick={() => setIsLoginModalOpen(true)} />} />
        
        {/* Rutas protegidas para usuarios normales */}
        <Route 
          path="/usuarios" 
          element={
            <ProtectedSuperAdminRoute currentUser={currentUser}>
              <AdminSettings user={currentUser} />
            </ProtectedSuperAdminRoute>
          } 
        />
        <Route
          path="/mis-reservas"
          element={
            <ProtectedUserRoute currentUser={currentUser}>
              <ReservasUsuarios user={currentUser} />
            </ProtectedUserRoute>
          }
        />
        
        {/* Ruta de fallback para 404 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLogin={handleLogin}
      />

      {chatbotEnabled && (
        <BotAvatar>
          <ChatBotUI
            onLogout={handleLogout}
            onLoginRequest={() => setIsLoginModalOpen(true)}
            onCloseLoginModal={() => setIsLoginModalOpen(false)}
          />
        </BotAvatar>
      )}
    </Layout>
  );
};

const AppWrapper = () => (
  <Router>
    <NotificationProvider>
      <AppInner />
    </NotificationProvider>
  </Router>
);

export default AppWrapper;
